export const funnelDimensions = {
	deck_creation: { entry: ["blank", "starter", "import"] },
	onboarding_processing: { entry: ["new", "resumed"] },
	onboarding_logo: {},
	onboarding_neutral: {},
	first_slide_paint: {},
	export_render: { artifact: ["pdf", "pptx"] },
} as const;

export type FunnelName = keyof typeof funnelDimensions;
export type FunnelDimensions<Name extends FunnelName> = {
	[Key in keyof (typeof funnelDimensions)[Name]]: (typeof funnelDimensions)[Name][Key] extends readonly (infer Value)[]
		? Value
		: never;
};
export type FunnelOutcome =
	| "started"
	| "succeeded"
	| "failed"
	| "timed_out"
	| "unconfirmed"
	| "abandoned";
export type FunnelAttempt = {
	readonly active: boolean;
	finish: (outcome: Exclude<FunnelOutcome, "started">) => void;
	discard: () => void;
};
const outcomes: readonly string[] = [
	"started",
	"succeeded",
	"failed",
	"timed_out",
	"unconfirmed",
	"abandoned",
];
export const funnelDeadlines: Record<FunnelName, number> = {
	deck_creation: 60_000,
	onboarding_processing: 20 * 60_000,
	onboarding_logo: 20 * 60_000,
	onboarding_neutral: 60_000,
	first_slide_paint: 20 * 60_000,
	export_render: 5 * 60_000,
};

export function validateFunnelEvent(
	name: unknown,
	payload: unknown,
): payload is Record<string, string | number> {
	if (typeof name !== "string" || !Object.hasOwn(funnelDimensions, name))
		return false;
	if (
		!payload ||
		typeof payload !== "object" ||
		Object.getPrototypeOf(payload) !== Object.prototype
	)
		return false;
	const values = payload as Record<string, unknown>;
	const dimensions: Record<string, readonly string[]> =
		funnelDimensions[name as FunnelName];
	const expected = [
		"outcome",
		...Object.keys(dimensions),
		...(values.outcome === "started" ? [] : ["duration_ms"]),
	];
	if (
		Reflect.ownKeys(payload).length !== expected.length ||
		!expected.every((key) => Object.hasOwn(values, key))
	)
		return false;
	if (typeof values.outcome !== "string" || !outcomes.includes(values.outcome))
		return false;
	if (values.outcome === "abandoned" && name !== "first_slide_paint")
		return false;
	for (const [key, allowed] of Object.entries(dimensions)) {
		if (typeof values[key] !== "string" || !allowed.includes(values[key]))
			return false;
	}
	return (
		values.outcome === "started" ||
		(typeof values.duration_ms === "number" &&
			Number.isInteger(values.duration_ms) &&
			values.duration_ms >= 0 &&
			values.duration_ms <= 3_600_000 &&
			values.duration_ms % 100 === 0)
	);
}

export function createFunnelAttempt<Name extends FunnelName>(
	name: Name,
	dimensions: FunnelDimensions<Name>,
	emit: (name: FunnelName, payload: Record<string, string | number>) => boolean,
	permitted: () => boolean,
	onSettled: () => void = () => {},
): FunnelAttempt {
	const startedAt = performance.now();
	const initial = { ...dimensions, outcome: "started" };
	let active =
		permitted() && validateFunnelEvent(name, initial) && emit(name, initial);
	let timer: ReturnType<typeof setTimeout> | undefined;
	const discard = () => {
		const wasActive = active;
		active = false;
		clearTimeout(timer);
		if (wasActive) onSettled();
	};
	const finish: FunnelAttempt["finish"] = (outcome) => {
		if (!active) return;
		discard();
		if (!permitted()) return;
		const duration_ms = Math.min(
			3_600_000,
			Math.max(0, Math.round((performance.now() - startedAt) / 100) * 100),
		);
		const payload = { ...dimensions, outcome, duration_ms };
		if (validateFunnelEvent(name, payload)) emit(name, payload);
	};
	if (active)
		timer = setTimeout(() => finish("timed_out"), funnelDeadlines[name]);
	return {
		finish,
		discard,
		get active() {
			return active;
		},
	};
}
